import { AccountType, AccountWithdrawalItem } from '../account/account';
import { IdIdentifier } from '../common/identifiers';

export enum ContestTradingSystem {
  MetaTraderContest = 'meta-trader-contest',
}

export enum TradingSystem {
  MatchTrader = 'match-trader',
  TraderEvolution = 'trader-evolution',
  MetaTraderFive = 'meta-trader-5',
  MetaTraderXP = 'meta-trader-xp',
  CTrader = 'c-trader',
  BlackArrowFutures = 'black-arrow-futures',
  MetaTraderContest = ContestTradingSystem.MetaTraderContest,
}

export interface TradingSystemCredentials {
  tsAccount?: string;
  tsAccountId?: string;
  tsUserId?: string;
}

export interface UserTradingSystemCredentials {
  login: string;
  password: string;
  externalId: string;
  tradingSystem: TradingSystem;
  accountType: AccountType;
}

export interface AccountBalanceChart {
  date: string;
  value: number;
  type: 'position' | 'deposit/withdraw';
}

export interface AccountStatsModelBase {
  accountId: string;
  totalTrades: number;
  profitTrades: number;
  longTrades: number;
  lossTrades: number;
  shortTrades: number;
  grossProfit: number;
  grossLoss: number;
  totalNetProfit: number;
  profitFactor: number;
  winRate: number;
  bestProfit: number;
  biggestLoss: number;
  avgProfit: number;
  avgLoss: number;
  expectancy: number;
  avgTradeSize: number;
  avgTradeDuration: number;
  totalQuantity: number;
  totalPositionDuration: number;
  totalFees: number;
  totalRebatedFees?: number;
  latestPositionDate?: Date;
  profitBySymbol?: Record<string, number>;
}

export interface AccountStats extends Partial<AccountStatsModelBase> {
  bestStock?: {
    symbol: string;
    profitAndLoss: number;
    day: string;
  };
  bestDay?: {
    total: number;
    day: string;
  };
  riskRewardRatio?: number;
  successRate?: number;
  profitableDays?: Date[];
}

export interface Rolling30DayPeriod {
  periodNumber: number; // 0, 1, 2, etc. (0-30, 30-60, 60-90, etc.)
  startDate: Date;
  endDate: Date;
  profit: number; // Absolute profit in USD
  profitPercentage: number; // Profit as percentage of baseBalance
}

export interface AccountBalance {
  balance: number;
  profitAndLoss: number;
  dailyProfitAndLoss: number;
  credits: number;
  profitCredits?: number;
  refundCredits?: number;
  equity: number;
  unrealized?: number;
  allowedDailyLosses?: number;
  invalidProfitAndLoss?: number;
  dailyInvalidProfitAndLoss?: number;
  snapshotBalance?: number;
  snapshotEquity?: number;
  consecutiveInactivityDays?: number;
  maxLoss?: number;
  baseMaxLoss?: number;
  baseBalance?: number;
  syncDate?: string;
  withdrawals?: AccountWithdrawalItem[];
  highWaterMark?: number;
  growthPercentage?: number;
  highWaterMarkPnl?: number;
  weeklyProfitAndLoss?: number;
  dailyROI?: number;
  rolling30DayPeriods?: Rolling30DayPeriod[];
  /** EOD Trailing max loss level - updated once per day at EOD */
  eodTrailingLevel?: number;
}

export interface AccountBalanceVerification {
  success: boolean;
  currentBalance: number;
  expectedBalance: number;
  difference: number;
}

export interface AccountDailyPerformance {
  marketValue: number;
  totalVolume: number;
}

export type MidnightValue = IdIdentifier & {
  equity: number;
  balance: number;
  date: Date;
};

export type AccountDetailsResponse = {
  name: string;
  country: string;
};

export type AccountReachedInactivity = {
  accountId: string;
  inactivity?: number;
};
